import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Card, Row, Col, Input, Layout, Empty, Badge } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { RootState } from 'Store'
import theme, { MyTheme } from 'Components/common/Theme'
import {
  selectCurrentUser,
  selectMyMerchants,
  selectMyMerchantsEvent,
  selectStaffPermissionEvent,
} from 'Store/user/selector'
import { MyMerchantReq, MerchantData } from 'Store/user/types'
import { merchantPermissionAPI, myMerchantsAPI, staffMerchantsAPI, staffPermissionAPI } from 'Store/user/api'
import Text from 'Components/text/Text'
import { MerchantAvatar } from 'Components/avatar'
import LoadingView from 'Components/modal/LoadingView'
import { clearEventAction, updateActiveMerchantAction } from 'Store/user/actions'
import HomePageMenu from 'Components/HomePageMenu'
import { getUserLogin } from 'helpers/account'
import { EventModal } from 'Components/modal'
import { Theme } from 'Components/common'
import { useNavigate } from 'react-router-dom'

const { Meta } = Card

type Props = {} & ReduxProps

type SearchProps = {
  txtSearch: string
}

const MyMerchant: React.FunctionComponent<Props> = props => {
  const { event, currentUser, merchants, staffPermissionEvent } = props
  const [merchantsFilter, setMerchantsFilter] = useState<MerchantData[]>([])
  const dispatch = useDispatch()
  const userInfo = getUserLogin()
  const colors = theme.colors
  const isStaff = userInfo && userInfo?.store_type === 'merchant_staff'
  const history = useNavigate()

  const onGetMyMerchants = (): void => {
    const req: MyMerchantReq = {}

    if (isStaff) {
      dispatch(staffMerchantsAPI(req, currentUser.accessToken))
    } else {
      dispatch(myMerchantsAPI(req, currentUser.accessToken))
    }
  }

  useEffect(() => {
    if (currentUser.accessToken && userInfo && userInfo?.store_type) {
      onGetMyMerchants()
    }
  }, [])

  useEffect(() => {
    setMerchantsFilter(merchants)
  }, [merchants])

  const onSelectMerchant = (merchant: MerchantData): void => {
    dispatch(updateActiveMerchantAction({ merchant }))
    if (isStaff) {
      dispatch(staffPermissionAPI({}, currentUser.accessToken, merchant.id || 0))
    } else {
      // history.push('/')
      dispatch(merchantPermissionAPI({}, currentUser.accessToken, merchant.id || 0))
    }
  }

  useEffect(() => {
    //For auto active merchant if user have 1 merchant
    if (merchants && merchants.length === 1 && !currentUser?.activeMerchant) {
      onSelectMerchant(merchants[0])
    }
  }, [merchants])

  const filterMerchant = (txtSearch: string): void => {
    const filter = merchants.filter((item: MerchantData) =>
      item.name.toLowerCase().includes(txtSearch.toLowerCase().trim()),
    )
    setMerchantsFilter(filter || [])
  }

  const searchContent = (
    <SearchSection justify="start">
      <Col span={24}>
        <SearchInput
          placeholder="ระบุชื่อร้านค้าที่ต้องการค้นหา"
          prefix={<SearchOutlined color="#fff" />}
          allowClear
          onChange={(evt: any) => filterMerchant(evt.target.value)}
        />
      </Col>
    </SearchSection>
  )

  const merchantsContent = (
    <MerchantList>
      <Row gutter={[16, 16]}>
        {merchantsFilter?.map((item: MerchantData, index: number) => {
          return (
            <Col md={8} xs={24} sm={12} key={index}>
              {item?.is_fc ? (
                <Badge.Ribbon text="ร้านค้าแฟรนไชส์" color={Theme.colors.primary}>
                  <MCard loading={false} key={index} onClick={(): void => onSelectMerchant(item)} hoverable>
                    <Meta
                      avatar={<MerchantAvatar size={40} logo={item.logo || ''} />}
                      title={item.name}
                      description={<MyText>{item.address}</MyText>}
                    />
                  </MCard>
                </Badge.Ribbon>
              ) : (
                <MCard loading={false} key={index} onClick={(): void => onSelectMerchant(item)} hoverable>
                  <Meta
                    avatar={<MerchantAvatar size={40} logo={item.logo || ''} />}
                    title={item.name}
                    description={<MyText>{item.address}</MyText>}
                  />
                </MCard>
              )}
            </Col>
          )
        })}
      </Row>
    </MerchantList>
  )

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    history('/', { replace: true })
  }

  return (
    <>
      <MyLayout key="mymerchant">
        <HomePageMenu active="mymerchants" history={history} />
        <EventModal
          event={[staffPermissionEvent]}
          enable={true}
          successCalback={onEventSuccess}
          errorCalback={clearEvent}
        />
        <MyRow align="top" justify="center">
          <MyCol md={18} sm={23} xs={23}>
            <MyTitle color={colors.white}>ร้านค้าของฉัน</MyTitle>
            <Text color={colors.white}>เลือกร้านค้า</Text>
            <MyChildren>
              <LoadingView isShow={event.IsLoading} />
              {searchContent}
              {merchantsFilter && merchantsFilter.length > 0 ? (
                merchantsContent
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<Text color={colors.white}>ไม่พบชื่อร้านค้าที่คุณค้นหา</Text>}
                />
              )}
            </MyChildren>
          </MyCol>
        </MyRow>
      </MyLayout>
    </>
  )
}

const MyLayout = styled(Layout)`
  background: ${(props: { theme: MyTheme }): string => props.theme.colors.backgroundGradient};
  height: 100vh;
  overflow-y: scroll;
`

const MyText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const MyRow = styled(Row)`
  height: 100vh;
  padding: 50px 0;
`

const MyCol = styled(Col)`
  text-align: center;
`

const MyTitle = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.white} !important;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.h3};
`

const MyChildren = styled.div`
  padding: 20px;
`

const MCard = styled(Card)`
  text-align: left;
  cursor: pointer;
  border-radius: 10px;
  height: 150px;
`

const MerchantList = styled.div`
  //max-height: 60vh;
  //overflow-x: scroll;
  // padding: 0 10px;
`

const SearchSection = styled(Row)`
  margin: 20px 0;
`

const SearchInput = styled(Input)`
  border-radius: 6px;
  border-color: transparent;
  height: 45px;
`

const mapStateToProps = (state: RootState): any => {
  return {
    event: selectMyMerchantsEvent(state),
    currentUser: selectCurrentUser(state),
    merchants: selectMyMerchants(state),
    staffPermissionEvent: selectStaffPermissionEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(MyMerchant)
